/**
 * What the ground item labels tell the game report (P2.8-T76), and the hold of the key that shows every label: the
 * pool's counts after each frame, how many texts, looks, stackings and layouts they have set so far, and, on request
 * only, every label held, as a browser scenario reads them.
 */

/**
 * One label as the report lists it: its item's handle, the name it draws, whether it is drawn, its tint and its
 * strength, where it stands in logical pixels at the UI scale it was placed at, its width in logical pixels, how many
 * device pixels it is nudged to clear the labels placed before it (negative up), and whether it sits in the UI layer.
 */
export interface GroundLabelReading {
  readonly handle: number;
  readonly text: string;
  readonly shown: boolean;
  readonly tint: number;
  readonly alpha: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly shift: number;
  readonly inUiLayer: boolean;
}

/**
 * The surface's report. `held` is whether the key that shows every label is held, which the scene writes as the key
 * goes down and up (`LabelKey`) and the surface reads each frame. `active`, `free`, `created` and `shown` are the
 * pool's labels held, waiting, made so far and drawn after the last frame; `texts` counts every name set on a label,
 * `looks` every tint or strength set, `checks` every time which labels show was worked out again (only when the local
 * character's tile, the item under the pointer, the hold or the items drawn changed), `stacks` every time the shown
 * labels were placed (those, the world zoom or the UI scale), and `layouts` every time a label was moved; `list`
 * reads every label held.
 */
export interface GroundLabelReport {
  held: boolean;
  active: number;
  free: number;
  created: number;
  shown: number;
  texts: number;
  looks: number;
  checks: number;
  stacks: number;
  layouts: number;
  list: () => readonly GroundLabelReading[];
}

/**
 * A report before the surface mounts.
 *
 * @returns The report, its list empty and the key not held.
 */
export function createGroundLabelReport(): GroundLabelReport {
  return { held: false, active: 0, free: 0, created: 0, shown: 0, texts: 0, looks: 0, checks: 0, stacks: 0, layouts: 0, list: () => [] };
}
